import type React from 'react';
import { useEffect, useState } from 'react';
import { BannerContext } from './BannerContext';
import type { BannerDefinition } from './banner.types';

const STORAGE_KEY_PREFIX = 'banner-dismissed';
const ANONYMOUS_BUCKET = 'anonymous';

function storageKey(userHandle: string | null): string {
  return `${STORAGE_KEY_PREFIX}:${userHandle ?? ANONYMOUS_BUCKET}`;
}

function readDismissedIds(userHandle: string | null): Set<string> {
  try {
    const stored = localStorage.getItem(storageKey(userHandle));
    return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
  } catch {
    return new Set<string>();
  }
}

function persistDismissedIds(userHandle: string | null, ids: Set<string>) {
  try {
    localStorage.setItem(storageKey(userHandle), JSON.stringify([...ids]));
  } catch {
    // localStorage unavailable
  }
}

export interface BannerProviderProps {
  children: React.ReactNode;
  userHandle?: string | null;
}

export function BannerProvider({ children, userHandle = null }: BannerProviderProps) {
  const [banners, setBanners] = useState<BannerDefinition[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => readDismissedIds(userHandle));

  useEffect(() => {
    setDismissedIds(readDismissedIds(userHandle));
  }, [userHandle]);

  const addBanner = (banner: BannerDefinition) => {
    setBanners((prev) => {
      const index = prev.findIndex((b) => b.id === banner.id);
      if (index === -1) {
        return [...prev, banner];
      }
      const next = [...prev];
      next[index] = banner;
      return next;
    });
  };

  const removeBanner = (id: string) => {
    setBanners((prev) => prev.filter((b) => b.id !== id));
  };

  const dismissBanner = (id: string) => {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistDismissedIds(userHandle, next);
      return next;
    });
  };

  const value = { banners, dismissedIds, addBanner, removeBanner, dismissBanner };

  return <BannerContext.Provider value={value}>{children}</BannerContext.Provider>;
}
